"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Wrench, Droplets, ShieldCheck, Clock, ArrowRight } from "lucide-react";
const commonProblems = [{
  id: "leaks",
  title: "Persistent Leaks and Drips",
  content: "A dripping faucet or a running toilet might seem like a minor annoyance, but it can waste hundreds of gallons of water annually, significantly increasing your utility bills. Leaks often stem from worn-out washers, O-rings, or corroded valve seats. Addressing these promptly prevents water damage to your cabinetry and flooring, and conserves a vital resource."
}, {
  id: "clogs",
  title: "Stubborn Clogs and Slow Drains",
  content: "Over time, hair, soap scum, grease, and food particles accumulate in your pipes, leading to slow drainage or complete blockages. While chemical drain cleaners offer a temporary fix, they can corrode your pipes over time. Professional snaking or hydro-jetting provides a safe, long-term solution by completely clearing the blockage without damaging your plumbing infrastructure."
}, {
  id: "pressure",
  title: "Low Water Pressure Issues",
  content: "If your shower feels more like a trickle, you might be dealing with low water pressure. This can be caused by a variety of factors, including mineral buildup in your aerators, a partially closed shut-off valve, or more serious issues like hidden leaks or corroded galvanized pipes. A professional assessment is crucial to pinpoint the exact cause and restore optimal flow."
}, {
  id: "heater",
  title: "Water Heater Malfunctions",
  content: "Inconsistent water temperatures, strange rumbling noises, or rusty water are clear indicators that your water heater needs attention. Sediment buildup at the bottom of the tank is a common culprit, reducing efficiency and potentially causing the tank to overheat. Regular flushing and anode rod inspections can extend the life of your unit."
}];
const maintenanceTips = ["Schedule annual professional plumbing inspections.", "Install drain catchers to prevent hair and debris buildup.", "Avoid pouring grease or oil down the kitchen sink.", "Know the location of your main water shut-off valve.", "Insulate exposed pipes during winter to prevent freezing.", "Flush your water heater annually to remove sediment."];
const servicedBrands = ["Kohler", "Moen", "Delta", "American Standard", "Grohe", "Pfister", "Rheem", "Bradford White"];
function RelatedPosts() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-80 rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground mt-8 p-4 bg-destructive/10 rounded-lg border border-destructive/20">
        Failed to load related articles.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return null;
  }
  return <div className="mt-12">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-default font-bold text-foreground">Related Plumbing Insights</h3>
        <Link to="/HomePage">
          <Button variant="ghost" className="hidden sm:flex">
            View All Articles <ArrowRight className="ml-2 size-4" />
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post, i) => <article key={post.id} data-index={i} className="group flex flex-col bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
            {post.featuredImage ? <div className="aspect-video overflow-hidden bg-muted">
                <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
              </div> : <div className="aspect-video bg-muted flex items-center justify-center">
                <Droplets className="size-12 text-muted-foreground/30" />
              </div>}
            <div className="p-6 flex flex-col flex-grow">
              <h4 className="font-default font-semibold text-lg mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h4>
              {post.excerpt && <p className="text-muted-foreground text-sm line-clamp-3 mb-4 flex-grow" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
              <div className="mt-auto pt-4 border-t border-border">
                <Link to={post.link || "#"} className="text-primary text-sm font-medium inline-flex items-center hover:underline">
                  Read Article <ArrowRight className="ml-1 size-3" />
                </Link>
              </div>
            </div>
          </article>)}
      </div>
      <div className="mt-6 text-center sm:hidden">
        <Link to="/HomePage">
          <Button variant="outline" className="w-full">
            View All Articles
          </Button>
        </Link>
      </div>
    </div>;
}
export default function PlumbingDeepDive() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="plumbingservicesdeepdive" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        
        {}
        <header className="mb-12 text-center max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-4">Comprehensive Guide</Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 leading-tight">
            The Ultimate Guide to Home Plumbing Maintenance &amp; Repair
          </h2>
          <p className="text-lg text-muted-foreground">
            Understanding your home's plumbing system is the first step to preventing costly disasters. Explore common issues, preventative maintenance, and why trusting Handy Heroes professionals makes all the difference.
          </p>
        </header>

        {}
        <div className="mb-16 rounded-2xl overflow-hidden shadow-lg border border-border">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_deep_dive_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_deep_dive_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_deep_dive_hero.webp-wvc-sizes" alt="Professional plumber working on pipes under a sink" className="w-full h-[400px] md:h-[500px] object-cover" loading="lazy" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {}
          <div className="lg:col-span-2 space-y-10">
            
            <section>
              <h2 className="text-3xl font-default font-bold text-foreground mb-4">Why Professional Plumbing Matters</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                While DIY projects can be rewarding, plumbing is an area where expertise is non-negotiable. A seemingly simple fix can quickly escalate into a major flood if handled incorrectly. Professional plumbers bring years of training, specialized tools, and a deep understanding of local building codes to every job.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Handy Heroes is a marketplace connecting homeowners with independent local pros. Pros join through network onboarding. Your pro gives a quote before work starts, helping you make informed decisions whether planning maintenance or scheduling a pipe repair. For complete home care, explore our <Link to="/HVACServicesPage" className="text-primary font-medium underline hover:opacity-80">HVAC Services</Link> and <Link to="/ElectricalServicesPage" className="text-primary font-medium underline hover:opacity-80">Electrical Services</Link>.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-3xl font-default font-bold text-foreground mb-6">Common Plumbing Problems Explained</h2>
              <Accordion type="single" collapsible className="w-full">
                {commonProblems.map((problem, index) => <AccordionItem key={problem.id} value={problem.id} data-index={index}>
                    <AccordionTrigger className="text-lg font-semibold hover:text-primary">
                      {problem.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pt-2 pb-4">
                      {problem.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </section>

            <Separator />

            <section>
              <h2 className="text-3xl font-default font-bold text-foreground mb-4">Preventative Maintenance Tips</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                The best plumbing repair is the one you never have to make. Implementing a routine maintenance schedule can save you thousands of dollars and countless headaches over the lifespan of your home. Pair your plumbing care with our seasonal <Link to="/HomeMaintenancePage" className="text-primary font-medium underline hover:opacity-80">Home Maintenance</Link> plans.
              </p>
              <div className="bg-secondary/20 rounded-xl p-6 border border-secondary/30">
                <ul className="space-y-3">
                  {maintenanceTips.map((tip, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                      <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium">{tip}</span>
                    </li>)}
                </ul>
              </div>
            </section>

          </div>

          {}
          <div className="space-y-8">
            
            {}
            <div className="bg-primary text-primary-foreground rounded-xl p-8 shadow-md text-center">
              <Droplets className="size-12 mx-auto mb-4 opacity-90" />
              <h3 className="text-2xl font-default font-bold mb-3">Need a Plumber Now?</h3>
              <p className="mb-6 text-primary-foreground/90 text-sm">
                Don't let a small leak become a major disaster. Connect with a verified professional today.
              </p>
              <Link to="/FindAProPage">
                <Button variant="secondary" size="lg" className="w-full font-bold">
                  Find a Pro
                </Button>
              </Link>
            </div>

            {}
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
              <h3 className="text-lg font-default font-bold text-foreground mb-4 flex items-center gap-2">
                <Wrench className="size-5 text-primary" />
                Brands We Service
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our network professionals are experienced with all major plumbing manufacturers and fixtures.
              </p>
              <div className="flex flex-wrap gap-2">
                {servicedBrands.map((brand, index) => <Badge key={index} data-index={index} variant="outline" className="bg-background">
                    {brand}
                  </Badge>)}
              </div>
            </div>

            {}
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
              <h3 className="text-lg font-default font-bold text-foreground mb-4 flex items-center gap-2">
                <Clock className="size-5 text-primary" />
                Why Choose Us
              </h3>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li className="flex items-center gap-3">
                  <div className="size-2 rounded-lg bg-primary shrink-0" />
                  Independent Local Pros
                </li>
                <li className="flex items-center gap-3">
                  <div className="size-2 rounded-lg bg-primary shrink-0" />
                  Quotes Before Work Begins
                </li>
                <li className="flex items-center gap-3">
                  <div className="size-2 rounded-lg bg-primary shrink-0" />
                  Simple Online Requests
                </li>
                <li className="flex items-center gap-3">
                  <div className="size-2 rounded-lg bg-primary shrink-0" />
                  Ask Your Pro About Availability
                </li>
              </ul>
            </div>

          </div>
        </div>

        <Separator className="my-16" />

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <RelatedPosts />
        </PostsProvider>

      </div>
    </section>;
}